import { useState } from "react";
import { Reveal, SectionHeader } from "./Reveal";
import { VIDEO_SECTION, VIDEOS, youtubeId } from "@/constants/videos";

function VideoCard({ video, index }) {
  const [playing, setPlaying] = useState(false);
  const ytId = video.type === "youtube" ? youtubeId(video.url) : "";
  const isFile = video.type === "file" && video.url;
  const playable = Boolean(ytId) || isFile;

  return (
    <div
      className="group relative overflow-hidden border border-white/10 bg-panel/40"
      data-testid={`video-card-${index}`}
    >
      <div className="relative aspect-video w-full bg-ink">
        {playing && ytId ? (
          <iframe
            src={`https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0`}
            title={video.title}
            className="absolute inset-0 h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            data-testid={`video-iframe-${index}`}
          />
        ) : playing && isFile ? (
          <video
            src={video.url}
            poster={video.poster}
            controls
            autoPlay
            className="absolute inset-0 h-full w-full object-cover"
            data-testid={`video-file-${index}`}
          />
        ) : (
          <button
            type="button"
            onClick={() => playable && setPlaying(true)}
            data-testid={`video-play-${index}`}
            className="absolute inset-0 h-full w-full"
            aria-label={playable ? `Play ${video.title}` : "Video coming soon"}
          >
            {video.poster ? (
              <img
                src={video.poster}
                alt={video.title}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            ) : null}
            <span className="absolute inset-0 bg-ink/45 transition-colors group-hover:bg-ink/30" />
            <span className="absolute inset-0 grid place-items-center">
              <span className="grid h-16 w-16 place-items-center rounded-full bg-crimson text-white shadow-xl transition-transform group-hover:scale-110">
                <i className="fa-solid fa-play ml-1 text-xl" />
              </span>
            </span>
            {!playable ? (
              <span className="absolute bottom-3 left-3 rounded-full border border-white/20 bg-ink/80 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-slate-300">
                Coming soon
              </span>
            ) : null}
          </button>
        )}
      </div>

      <div className="p-5">
        <h3 className="font-display text-lg font-semibold uppercase leading-tight text-white">{video.title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-slate-400">{video.description || video.caption}</p>
        {video.features && video.features.length ? (
          <ul className="mt-4 space-y-2">
            {video.features.map((f, j) => (
              <li key={j} className="flex items-start gap-2.5 text-[13px] leading-snug text-slate-300">
                <i className="fa-solid fa-circle-check mt-0.5 text-xs text-crimson" />
                {f}
              </li>
            ))}
          </ul>
        ) : null}
        <a
          href={video.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          data-testid={`video-watch-link-${index}`}
          className="mt-5 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.14em] text-steel transition-colors hover:text-white"
        >
          <i className="fa-brands fa-youtube text-sm text-crimson" />
          Watch on YouTube
        </a>
      </div>
    </div>
  );
}

export default function VideoGallery() {
  return (
    <section id="videos" className="border-t border-white/10 bg-navy/20 py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeader eyebrow={VIDEO_SECTION.eyebrow} title={VIDEO_SECTION.title} />
        <Reveal delay={0.1}>
          <p className="mt-6 max-w-2xl leading-relaxed text-slate-300">{VIDEO_SECTION.subtitle}</p>
        </Reveal>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {VIDEOS.map((v, i) => (
            <Reveal key={v.id} delay={0.08 * i}>
              <VideoCard video={v} index={i} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
